import React from 'react';
import { Clock, ArrowRight, TrendingUp, BookOpen, Users } from 'lucide-react';
import './BlogSection.css';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
  featured: boolean;
}

const BlogSection: React.FC = () => {
  const blogPosts: BlogPost[] = [
    {
      id: 13,
      title: "Option Chain Reading Made Simple: A Daily Workflow for Better Entries",
      excerpt: "Learn a practical way to read option chain data with open interest, volume, and strike behavior to improve trade timing in NIFTY and BANK NIFTY.",
      author: "Deepa",
      date: "2026-04-16",
      readTime: "10 min read",
      category: "Options Theory",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 14,
      title: "From Overtrading to Consistency: 7 Habits Every FNO Trader Should Build",
      excerpt: "A behavior-focused guide for traders who want stable performance by reducing impulsive trades, improving journaling, and following repeatable routines.",
      author: "Biraj",
      date: "2026-04-12",
      readTime: "9 min read",
      category: "Trading Psychology",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 15,
      title: "Expiry Week Plan: How to Trade Thursday Volatility Without Over-Risking",
      excerpt: "A step-by-step plan for handling expiry week volatility with strict position sizing, scenario planning, and clear no-trade conditions.",
      author: "Rajesh",
      date: "2026-04-08",
      readTime: "12 min read",
      category: "Expiry Trading",
      image: "https://images.unsplash.com/photo-1556155092-490a1ba16284?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 11,
      title: "Weekly FNO Setup Playbook: Pre-Market Checklist for Consistent Trades",
      excerpt: "A practical pre-market routine for FNO traders in India. Learn how to build a repeatable checklist using trend, volatility, levels, and risk controls before placing your first trade.",
      author: "Prakash",
      date: "2026-04-28",
      readTime: "11 min read",
      category: "Risk Management",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=250&fit=crop",
      featured: true
    },
    {
      id: 12,
      title: "Bank NIFTY Intraday Framework: Level-Based Entries with Defined Risk",
      excerpt: "Build a structured intraday workflow for Bank NIFTY options with entry triggers, invalidation levels, position sizing, and post-trade review rules.",
      author: "Sharada",
      date: "2026-04-20",
      readTime: "13 min read",
      category: "Index Options",
      image: "https://images.unsplash.com/photo-1554224154-22dec7ec8818?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 1,
      title: "Options Buying Strategies: Complete Guide for NSE & BSE Markets",
      excerpt: "Master the art of options buying with proven strategies for Indian stock exchanges. Learn call and put options, timing, and risk management techniques.",
      author: "Sharada",
      date: "2024-01-15",
      readTime: "12 min read",
      category: "Options Trading",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop",
      featured: true
    },
    {
      id: 2,
      title: "FNO Trading Fundamentals: Futures & Options for Beginners",
      excerpt: "Complete beginner's guide to FNO trading in India. Understand futures contracts, options basics, and how to start your FNO trading journey.",
      author: "Prakash",
      date: "2024-01-12",
      readTime: "15 min read",
      category: "FNO Basics",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=250&fit=crop",
      featured: true
    },
    {
      id: 3,
      title: "Advanced Options Strategies: Straddles, Strangles & Iron Condors",
      excerpt: "Learn sophisticated options strategies including straddles, strangles, and iron condors specifically designed for Indian market volatility patterns.",
      author: "Rajesh",
      date: "2024-01-10",
      readTime: "18 min read",
      category: "Advanced Strategies",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 4,
      title: "Risk Management in FNO Trading: Position Sizing & Stop Losses",
      excerpt: "Essential risk management techniques for FNO traders. Learn position sizing, stop-loss strategies, and capital preservation methods.",
      author: "Biraj",
      date: "2024-01-08",
      readTime: "10 min read",
      category: "Risk Management",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 5,
      title: "NIFTY & BANK NIFTY Options: Trading Strategies for Index Options",
      excerpt: "Specialized strategies for trading NIFTY and BANK NIFTY options. Understand index options characteristics and profitable trading approaches.",
      author: "Deepa",
      date: "2024-01-05",
      readTime: "14 min read",
      category: "Index Options",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 6,
      title: "Options Greeks Explained: Delta, Gamma, Theta & Vega for Indian Markets",
      excerpt: "Master the options Greeks and understand how they affect your options positions in Indian market conditions with practical examples.",
      author: "Sharada",
      date: "2024-01-03",
      readTime: "16 min read",
      category: "Options Theory",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 7,
      title: "Futures Trading Strategies: Hedging & Speculation Techniques",
      excerpt: "Learn effective futures trading strategies including hedging techniques and speculative approaches for Indian commodity and equity futures.",
      author: "Prakash",
      date: "2024-01-01",
      readTime: "13 min read",
      category: "Futures Trading",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 8,
      title: "Options Expiry Trading: Strategies for Monthly Expiry Days",
      excerpt: "Specialized strategies for trading around options expiry days. Learn how to capitalize on volatility and time decay during expiry week.",
      author: "Rajesh",
      date: "2023-12-28",
      readTime: "11 min read",
      category: "Expiry Trading",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 9,
      title: "Building a Profitable FNO Portfolio: Asset Allocation & Strategy Mix",
      excerpt: "Learn how to build a diversified FNO portfolio with proper asset allocation across different strategies and risk levels.",
      author: "Biraj",
      date: "2023-12-25",
      readTime: "9 min read",
      category: "Portfolio Management",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=250&fit=crop",
      featured: false
    },
    {
      id: 10,
      title: "Psychology of FNO Trading: Managing Emotions & Mental Discipline",
      excerpt: "Master the psychological aspects of FNO trading. Learn to control emotions, maintain discipline, and develop a winning mindset.",
      author: "Deepa",
      date: "2023-12-22",
      readTime: "8 min read",
      category: "Trading Psychology",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop",
      featured: false
    }
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Options Trading': '#3b82f6',
      'FNO Basics': '#f59e0b',
      'Advanced Strategies': '#10b981',
      'Risk Management': '#8b5cf6',
      'Index Options': '#ef4444',
      'Options Theory': '#06b6d4',
      'Futures Trading': '#8b5cf6',
      'Expiry Trading': '#f59e0b',
      'Portfolio Management': '#10b981',
      'Trading Psychology': '#ef4444'
    };
    return colors[category] || '#6b7280';
  };

  const featuredPosts = blogPosts.filter(post => post.featured);
  const regularPosts = blogPosts.filter(post => !post.featured);

  return (
    <section className="blog-section section" id="blog">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">FNO Trading Insights</h2>
          <p className="section-subtitle">
            Master Indian stock exchange Futures & Options trading with expert strategies and insights
          </p>
        </div>

        <div className="blog-stats">
          <div className="stat-item">
            <BookOpen className="stat-icon" />
            <div className="stat-content">
              <span className="stat-number">200+</span>
              <span className="stat-label">Expert Articles</span>
            </div>
          </div>
          <div className="stat-item">
            <Users className="stat-icon" />
            <div className="stat-content">
              <span className="stat-number">50K+</span>
              <span className="stat-label">Monthly Readers</span>
            </div>
          </div>
          <div className="stat-item">
            <TrendingUp className="stat-icon" />
            <div className="stat-content">
              <span className="stat-number">98%</span>
              <span className="stat-label">Success Rate</span>
            </div>
          </div>
        </div>

        <div className="featured-posts">
          <h3 className="section-subtitle">Featured Articles</h3>
          <div className="featured-grid">
            {featuredPosts.map((post, index) => (
              <article key={post.id} className={`featured-card ${index === 0 ? 'main-featured' : ''}`}>
                <div className="post-image">
                  <img src={post.image} alt={post.title} />
                  <div className="post-category" style={{ backgroundColor: getCategoryColor(post.category) }}>
                    {post.category}
                  </div>
                </div>
                <div className="post-content">
                  <h4 className="post-title">{post.title}</h4>
                  <p className="post-excerpt">{post.excerpt}</p>
                  <div className="post-meta">
                    <div className="author-info">
                      <span className="author-name">{post.author}</span>
                      <span className="post-date">{formatDate(post.date)}</span>
                    </div>
                    <div className="read-info">
                      <Clock className="read-icon" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <a href={`/blog/${post.id}`} className="read-more">
                    Read More
                    <ArrowRight className="arrow-icon" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="regular-posts">
          <h3 className="section-subtitle">Latest Articles</h3>
          <div className="posts-grid">
            {regularPosts.map((post) => (
              <article key={post.id} className="post-card">
                <div className="post-image">
                  <img src={post.image} alt={post.title} />
                  <div className="post-category" style={{ backgroundColor: getCategoryColor(post.category) }}>
                    {post.category}
                  </div>
                </div>
                <div className="post-content">
                  <h4 className="post-title">{post.title}</h4>
                  <p className="post-excerpt">{post.excerpt}</p>
                  <div className="post-meta">
                    <div className="author-info">
                      <span className="author-name">{post.author}</span>
                      <span className="post-date">{formatDate(post.date)}</span>
                    </div>
                    <div className="read-info">
                      <Clock className="read-icon" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <a href={`/blog/${post.id}`} className="read-more">
                    Read More
                    <ArrowRight className="arrow-icon" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="blog-cta">
          <div className="cta-content">
            <h3>Master FNO Trading with Expert Guidance</h3>
            <p>Get the latest FNO strategies, options insights, and trading techniques delivered to your inbox</p>
          </div>
          <div className="cta-actions">
            <a href="#newsletter" className="btn btn-primary">
              Join FNO Community
            </a>
            <a href="#platform" className="btn btn-outline">
              View All Strategies
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
